// Chatbot.jsx
import React, { useState, useEffect, useRef } from "react";
import { bot_data } from "../chatbot/bot_data";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "How can I assist you?" },
  ]);
  const [input, setInput] = useState("");
  
  // Reference for messages container to auto-scroll
  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close chat when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatWindowRef.current && !chatWindowRef.current.contains(event.target) && 
          !event.target.closest('.chat-icon')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle chat open/close
  const toggleChat = () => setIsOpen(!isOpen);

  // Handle user sending message
  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = { from: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);

    const botResponse = getBotResponse(input);
    setTimeout(() => {
      setMessages((prev) => [...prev, { from: "bot", text: botResponse }]);
    }, 500);
    
    setInput("");
  };

  // Format single course as paragraph
  const formatCourseParagraph = (course) => {
    return `Title: ${course.title} | Duration: ${course.duration} | Price: ${course.price} | Availability: ${course.availability} | Description: ${course.description} | Level: ${course.level} | Schedule: ${course.schedule}`;
  };

  // Simple bot logic with conversational responses
  const getBotResponse = (message) => {
    const msg = message.toLowerCase().trim();

    // Registration/Contact queries
    if (msg.includes("register") || msg.includes("enroll") || msg.includes("sign up") || 
        msg.includes("contact") || msg.includes("call") || msg.includes("email") || 
        msg.includes("how to join") || msg.includes("admission")) {
      return "To register for any course, please fill out the form above to show your interest, or contact us at:\nPhone: +92 339 155 2155\nEmail: ceo@onlinequranandislamicinstitue.com\nOur team will get back to you within 24 hours.";
    }

    // Greetings
    if (msg === "hi" || msg === "hello" || msg === "hey") {
      return "Hello! How can I help you today?";
    }
    
    if (msg === "salam" || msg === "asalam o alikum" || msg === "assalamu alaikum") {
      return "Wa Alaikum Assalam! How can I assist you with our courses?";
    }
    
    if (msg.includes("how are you")) {
      return "I'm fine, thank you! Ready to help you find the perfect Quran course. What are you interested in?";
    }

    // If user asks about all courses (list)
    if (msg.includes("courses") || msg.includes("all") || msg.includes("list") || msg.includes("available")) {
      const courseList = bot_data
        .map((course) => formatCourseParagraph(course))
        .join("\n\n");
      
      return `Here are all our available courses:\n\n${courseList}\n\nTo register for any course, please fill out the form above or contact us at +92 339 155 2155`;
    }

    // Quran Tafseer specific
    if (msg.includes("tafseer") || msg.includes("tafsir") || (msg.includes("quran") && msg.includes("meaning"))) {
      const tafseerCourse = bot_data.find(c => c.title.includes("Quran Tafseer"));
      return formatCourseParagraph(tafseerCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }
    
    // Quran Memorization / Hifz specific
    if (msg.includes("memorization") || msg.includes("hifz") || msg.includes("memorize") || (msg.includes("quran") && msg.includes("by heart"))) {
      const hifzCourse = bot_data.find(c => c.title.includes("Hifz-e-Quran"));
      return formatCourseParagraph(hifzCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Tajweed specific
    if (msg.includes("tajweed")) {
      const tajweedCourse = bot_data.find(c => c.title.includes("Tajweed"));
      return formatCourseParagraph(tajweedCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Basic Reading specific
    if (msg.includes("basic") || msg.includes("reading")) {
      const basicCourse = bot_data.find(c => c.title.includes("Basic Quran Reading"));
      return formatCourseParagraph(basicCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Seerah specific
    if (msg.includes("seerah") || (msg.includes("prophet") && msg.includes("life"))) {
      const seerahCourse = bot_data.find(c => c.title.includes("Seerah"));
      return formatCourseParagraph(seerahCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Dua specific
    if (msg.includes("dua") || msg.includes("supplication")) {
      const duaCourse = bot_data.find(c => c.title.includes("Dua"));
      return formatCourseParagraph(duaCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Islamic History specific
    if (msg.includes("history") && msg.includes("islam")) {
      const historyCourse = bot_data.find(c => c.title.includes("Islamic History"));
      return formatCourseParagraph(historyCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Islamic Studies specific
    if (msg.includes("islamic studies")) {
      const studiesCourse = bot_data.find(c => c.title.includes("Islamic Studies"));
      return formatCourseParagraph(studiesCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Quran Translation specific
    if (msg.includes("translation") || (msg.includes("quran") && msg.includes("translate"))) {
      const translationCourse = bot_data.find(c => c.title.includes("Quran Translation"));
      return formatCourseParagraph(translationCourse) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Check if user asks about any specific course by title (generic fallback)
    const course = bot_data.find((c) => c.title.toLowerCase().includes(msg));
    if (course) {
      return formatCourseParagraph(course) + "\n\nTo register for this course, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Check for price inquiries
    if (msg.includes("price") || msg.includes("cost") || msg.includes("fee")) {
      const course = bot_data.find(c => msg.includes(c.title.toLowerCase()));
      if (course) {
        return `${course.title} costs ${course.price} per session. To register, please fill out the form above or contact us at +92 339 155 2155`;
      }
      return "Our courses range from $30 to $50 per session. Which course are you interested in? You can also contact us at +92 339 155 2155 for more information.";
    }

    // Check for instructor inquiries
    if (msg.includes("who") && msg.includes("teach")) {
      return "We have qualified instructors including Hafiz Bilal, Hafiz Qasim, Molana Bilal, and more. All our teachers are certified in Quranic studies. To register, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Farewell
    if (msg.includes("bye") || msg.includes("goodbye") || msg.includes("thank")) {
      return "You're welcome! Feel free to ask if you have more questions. May Allah bless your learning journey! For registration, please fill out the form above or contact us at +92 339 155 2155";
    }

    // Default response with helpful suggestions
    return "I'm here to help! You can ask me about:\n• Tajweed course\n• Quran Tafseer\n• Quran Memorization (Hifz)\n• Basic Quran Reading\n• Registration & Contact\n• Or type 'courses' to see all options\n\nFor registration, contact: +92 339 155 2155 or email: ceo@onlinequranandislamicinstitue.com";
  };

  return (
    <>
      {/* Improved Circular Chat Icon with Bot in Center */}
      <div
        onClick={toggleChat}
        className="chat-icon fixed right-6 bottom-6 w-16 h-16 rounded-full bg-white border-2 border-green-400 shadow-xl flex items-center justify-center cursor-pointer z-50 hover:scale-110 transition-transform duration-300 hover:shadow-2xl group"
      >
        {/* Bot Icon with subtle animation */}
        <div className="relative">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="w-8 h-8 text-green-500 group-hover:text-green-600 transition-colors" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
            />
          </svg>
          {/* Small green dot for "online" status */}
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-400 border-2 border-white rounded-full animate-pulse"></span>
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatWindowRef}
          className="fixed right-6 bottom-24 w-80 md:w-96 bg-white border border-green-300 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden"
          style={{ height: '480px' }}
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold p-4 flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-green-200 rounded-full animate-pulse"></div>
            <span>Quran Courses Assistant</span>
            <button 
              onClick={() => setIsOpen(false)}
              className="ml-auto text-white hover:text-green-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages - with auto-scroll */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.from === "bot" ? "justify-start" : "justify-end"}`}
              >
                <div
                  className={`p-3 rounded-lg whitespace-pre-line max-w-[85%] text-sm ${
                    msg.from === "bot" 
                      ? "bg-white text-gray-800 rounded-tl-none shadow-sm border border-gray-100" 
                      : "bg-gradient-to-r from-green-500 to-green-600 text-white rounded-tr-none shadow-sm"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {/* This div enables auto-scroll to bottom */}
            <div ref={messagesEndRef} />
          </div>

          {/* Input with Send Arrow Button */}
          <div className="flex items-center p-3 border-t border-green-200 bg-white">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 border border-gray-200 rounded-l-xl px-4 py-3 outline-none text-sm focus:border-green-400 focus:ring-1 focus:ring-green-400 bg-gray-50"
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button
              onClick={handleSend}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-r-xl hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center min-w-[52px] shadow-md"
              aria-label="Send message"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="w-5 h-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" 
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;